/**
 * Copyright (C) 2017-2018 System Clinic Inc. All rights reserved.
 * This file is the property of System Clinic Inc.
 *
 * File: Signup.js
 * Author: Naoaki Suganuma
 * Update: 2018/3/12
 * Version: 1.0.0
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import ValidatorGroup from '../validation/ValidatorGroup';
import TextFieldValidator from '../validation/TextFieldValidator';
import Typography from '@material-ui/core/Typography';
import withStyles from '@material-ui/core/styles/withStyles';
import withTheme from '../withTheme';
import { signupIfNeeded, clearSignup } from '../actions/auth';

/**
 * スタイル情報 
 */
const styles = theme => ({
  base: {
    flex: "1 1 auto",
    display: "flex",
    position: "relative",
    overflow: "hidden",
  },
  frame: {
    minWidth: 480,
    maxWidth: 540,
    height: 'auto',
    position: 'absolute',
    top: '20%',
    left: 0,
    right: 0,
    margin: 'auto',
  },
  paper: {
    padding: 30,
    overflow: 'auto',
  },
  textField: {
    marginLeft: 0,
    width: "100%",
  },  
  signup: {
    float: 'right',
    marginTop: 30,
    width: 200,
  },
  aux: {
    paddingTop: 50,
    textAlign: "center"
  },
  error: {
    width: "100%",
    textAlign: "center",
    margin: "15px 0px",
    padding: "5px 0",
    border: "solid 3px",
    borderColor: theme.palette.error.light,
    borderRadius: "3px"
  },
  errorText: {
    width: "100%",
    textAlign: "center",
    marginTop: 3
  },});

/**
 * Signupコンポーネント
 */
class Signup extends Component {
  /**
    * コンストラクタ
    */
  constructor(props) {
    super(props);
    this.state = {
      email: "",
      emailEmpty: false,
    };
  }

  /**
   * Componentがアンマウントされる時に呼び出されるハンドラ
   */
  componentWillUnmount() {
    this.props.clearSignup();
  }

  /**
   * メールアドレスが変更された時に呼び出されるハンドラ
   */
  handleEmailChange = (event) => {
    this.setState({
      email: event.target.value,
      emailEmpty: false
    });
  }

  /**
   * サインアップボタンがクリックされた時に呼び出されるハンドラ
   */
  handleSignupButtonClick = (event) => {
    if (this.isEmpty(this.state.email)) {
      this.setState({ emailEmpty: true });
    } else {
      this.props.signupIfNeeded(this.state.email);
    }
  }

  /*
   * 空文字かどうかの判定
   */
  isEmpty(value) {
    if (value === undefined || value == null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '' || value.trim().length === 0;
    }
    return false;
  }

  /**
   * エラーメッセージを取得する。
   * @param {エラー}} error 
   */
  getErrorMessage(error) {
    if (error === "exists") {
      return "すでに登録されているメールアドレスです。";
    } else {
      return "エラーが発生しました。再度ユーザ登録を行ってください。";
    }
  }

  /**
   * Signupコンポーネントのレンダリング
   */
  render() {
    return (
      <div className={this.props.classes.base}>
        <div className={this.props.classes.frame}>
          {(() => {
            return this.props.signupComplete ?
              <Paper className={this.props.classes.paper}>
                <Typography variant="subtitle1">
                  登録したメールアドレスに確認のメールが届きますので、その内容にしたがって確認手続きを行ってください。確認手続きが完了すると正式登録となり、GLYPHSHOTの機能をご利用いただけます。
                </Typography>
              </Paper>
              :
              <Paper className={this.props.classes.paper}>
                {(() => {
                  return this.props.signupError != null ?
                    <div className={this.props.classes.error}>
                      <Typography variant="subtitle1" color="error" className={this.props.classes.errorText}>{this.getErrorMessage(this.props.signupError)}</Typography>
                    </div>
                    : null
                })()}
                {(() => {
                  return this.state.emailEmpty ?
                    <div className={this.props.classes.error}>
                      <Typography variant="subtitle1" color="error" className={this.props.classes.errorText}>メールアドレスを入力してください</Typography>
                    </div>
                    : null
                })()}
                <Typography variant="subtitle1">
                  ユーザ登録を行います。メールアドレスを入力してユーザ登録ボタンをクリックしてください。
                </Typography>
                <ValidatorGroup validatorGroupListener={this.handleGroupValidation}>
                  <TextFieldValidator
                    name="email"
                    label="メールアドレス"
                    value={this.state.email}
                    autoComplete="email"
                    onChange={this.handleEmailChange}
                    className={this.props.classes.textField}
                    fullWidth
                    margin="normal"
                    validators={['isEmail']}
                    errorMessages={['正しいメールアドレスを入力して下さい']}
                  />
                </ValidatorGroup>
                <Button
                  variant="contained"
                  color="primary"
                  className={this.props.classes.signup}
                  onClick={this.handleSignupButtonClick}
                >
                  ユーザ登録
              </Button>
            </Paper>
          })()}
          <div className={this.props.classes.aux}>
            <Button
              component={Link} to="/"
              color="primary"
            >
              ログイン画面に戻る
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

/**
 * StoreをPropsにマップする。
 * @param {*} state 
 */
function mapStateToProps(state) {
  return state.auth;
}

/**
 * Dispatcherをマップする。
 * @param {*} dispatch 
 */
function mapDispatchToProps(dispatch) {
  return {
    signupIfNeeded: (email) => {
      dispatch(signupIfNeeded(email))
    },
    clearSignup: () => {
      dispatch(clearSignup())
    }
  }
}

/**
 * reduxとの接続
 */
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTheme(withStyles(styles)(Signup)))